import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  OBRecord,
  BreakLinkResponse,
} from '../../services/api/domains/products/types';
import { formatDateOnly } from '../../utils/dateConversion';
import { GRAY_500, GRAY_900 } from 'theme/grayScale';
import { ORANGE_BOOK_PANEL_COLORS } from 'theme/sourcePanelColors';
import LgmButton from '../LgmButton';
import CollapsibleSection from './CollapsibleSection';
import SourcePanelEmptyState from './SourcePanelEmptyState';
import BreakLinkConfirmDialog from './BreakLinkConfirmDialog';
import TeCodeHeaderTooltip from '../TeCodeHeaderTooltip';
import TeCodeValue from '../TeCodeValue';
import ApplTypeHeaderTooltip from '../ApplTypeHeaderTooltip';
import ApplTypeValue from '../ApplTypeValue';
import ObTypeHeaderTooltip from '../ObTypeHeaderTooltip';
import ObTypeValue from '../ObTypeValue';

// ---------------------------------------------------------------------------
// Config type — 13.1.1
// ---------------------------------------------------------------------------

export interface OBPanelFieldConfig {
  /** Must be a valid OBRecord key; used as React key. `compute` overrides the value lookup. */
  key: keyof OBRecord;
  label: string;
  /** When true the field is always visible; when false it appears inside the secondary accordion. */
  defaultVisible: boolean;
  /** Optional formatter applied to the raw field value. */
  format?: (val: unknown) => string;
  /**
   * Optional computed value function. When provided, overrides the normal `record[key]` lookup.
   * Used for fields derived from multiple OBRecord properties (e.g. Application = appl_type + appl_no).
   */
  compute?: (record: OBRecord) => string;
}

const OB_PANEL_FIELDS: OBPanelFieldConfig[] = [
  // 4 default-visible fields
  {
    key: 'trade_name',
    label: 'Trade Name',
    defaultVisible: true,
  },
  {
    key: 'ingredient',
    label: 'Ingredient',
    defaultVisible: true,
  },
  {
    key: 'appl_type',
    label: 'Application Type',
    defaultVisible: true,
  },
  {
    key: 'appl_no',
    label: 'Application No.',
    defaultVisible: true,
  },
  {
    key: 'approval_date',
    label: 'Approval Date',
    defaultVisible: true,
    format: (val: unknown) => formatDateOnly(val) || '—',
  },
  {
    key: 'te_code',
    label: 'TE Code',
    defaultVisible: true,
  },
  // 5 accordion-hidden fields
  {
    key: 'strength',
    label: 'Strength',
    defaultVisible: false,
  },
  {
    key: 'df',
    label: 'Dosage Form',
    defaultVisible: false,
  },
  {
    key: 'route',
    label: 'Route',
    defaultVisible: false,
  },
  {
    key: 'product_no',
    label: 'Product No.',
    defaultVisible: false,
  },
  {
    key: 'applicant_full_name',
    label: 'Applicant',
    defaultVisible: false,
  },
  // Rendered as JSX (ObTypeValue), same as te_code/appl_type above -- no
  // `format` needed here (this panel's own render body special-cases
  // this key).
  {
    key: 'type',
    label: 'Listing Status',
    defaultVisible: false,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveValue(field: OBPanelFieldConfig, record: OBRecord): string {
  if (field.compute) return field.compute(record);
  const raw = record[field.key];
  if (raw === null || raw === undefined || raw === '') return '—';
  if (field.format) return field.format(raw) || '—';
  return String(raw);
}

function recordLabel(record: OBRecord): string {
  return (
    record.trade_name ||
    `${record.appl_type ?? ''}${record.appl_no ?? ''}`.trim() ||
    'Orange Book record'
  );
}

// ---------------------------------------------------------------------------
// Sub-component — a single read-only label/value row
// ---------------------------------------------------------------------------

const FieldRow: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      gap: '8px',
      padding: '4px 0',
      fontSize: '14px',
      lineHeight: '1.4',
    }}>
    <span
      style={{
        minWidth: '120px',
        fontWeight: 600,
        color: GRAY_500,
        flexShrink: 0,
      }}>
      {label}
    </span>
    <span style={{ color: GRAY_900, wordBreak: 'break-word' }}>{value}</span>
  </div>
);

// ---------------------------------------------------------------------------
// OrangeBookPanel — 13.1.2, extended by issue #622 Task 8 (three-state
// matrix, collapse behaviour, break-link control)
// ---------------------------------------------------------------------------

interface OrangeBookPanelProps {
  productId: number;
  obRecords: OBRecord[];
  /** Admin AND edit mode — both gate the break-link icon and the add-link control. */
  isAdmin: boolean;
  isEditMode: boolean;
  /**
   * Opens the Task 9 Link-to-Source modal, scoped to Orange Book. That modal
   * doesn't exist yet (issue #622 Task 9 is separate work) — this is a
   * placeholder trigger point only.
   */
  onAddLink: () => void;
}

/**
 * FDA Orange Book section for the Product modal. Always renders (issue
 * #622, Sub-task 8.1) — a product can legitimately link to several Orange
 * Book records (e.g. brand + generic), rendering one field block per record
 * under a single shared header, matching DMFPanel's established
 * multi-record pattern (issue #622, Sub-task 5.1; was previously restricted
 * to `obRecords[0]`). FDA is the authoritative source — no OB data here is
 * directly editable; the only write action is breaking a link (Sub-task 8.3).
 */
const OrangeBookPanel: React.FC<OrangeBookPanelProps> = ({
  productId,
  obRecords,
  isAdmin,
  isEditMode,
  onAddLink,
}) => {
  const [expandedByIndex, setExpandedByIndex] = React.useState<
    Record<number, boolean>
  >({});
  // Locally tracks records broken during this modal session — mirrors
  // CASRegistryPanel's `localEstablished` pattern rather than forcing a full
  // product refetch after every break (parent props only refresh on the
  // next full product fetch).
  const [brokenIds, setBrokenIds] = React.useState<Set<number>>(new Set());
  const [breakTarget, setBreakTarget] = React.useState<{
    recordId: number;
    label: string;
  } | null>(null);

  // Also reset on a genuinely fresh `obRecords` array (a real refetch, e.g.
  // after Task 9's link-create refresh) -- not just productId. Otherwise
  // breaking then re-linking the SAME underlying record within one modal
  // session leaves it permanently filtered out here: brokenIds is additive
  // and this was its only reset path, so a resurrected record with the same
  // fda_product_id stayed hidden even though fresh server data proved it
  // linked again. Found live via the Playwright MCP (issue #622).
  React.useEffect(() => {
    setBrokenIds(new Set());
    setBreakTarget(null);
  }, [productId, obRecords]);

  const canWrite = isAdmin && isEditMode;
  const records = obRecords.filter(r => !brokenIds.has(r.fda_product_id));

  if (records.length === 0) {
    return (
      <SourcePanelEmptyState
        badgeText="FDA"
        sourceLabel="Orange Book"
        palette={ORANGE_BOOK_PANEL_COLORS}
        canAdd={canWrite}
        onAdd={onAddLink}
      />
    );
  }

  const visibleFields = OB_PANEL_FIELDS.filter(f => f.defaultVisible);
  const hiddenFields = OB_PANEL_FIELDS.filter(f => !f.defaultVisible);

  const handleBroken = (result: BreakLinkResponse) => {
    setBrokenIds(prev => new Set(prev).add(result.record_id));
    setBreakTarget(null);
  };

  return (
    <div
      style={{
        margin: '20px 0 8px 0',
        border: `1px solid ${ORANGE_BOOK_PANEL_COLORS.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: ORANGE_BOOK_PANEL_COLORS.background,
      }}>
      {/* Panel header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: ORANGE_BOOK_PANEL_COLORS.headerBackground,
          borderBottom: `1px solid ${ORANGE_BOOK_PANEL_COLORS.border}`,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: ORANGE_BOOK_PANEL_COLORS.badgeText,
              backgroundColor: ORANGE_BOOK_PANEL_COLORS.badgeBackground,
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
            FDA - Orange Book
          </span>
        </div>
        {/* Orange Book genuinely supports multiple simultaneous links
            (Sub-task 5.1) -- the zero-link empty state's add button
            (SourcePanelEmptyState) only covers going from 0 to 1. Without
            this, an admin with an existing link has no way to add a second
            one. Found live via the Playwright MCP (issue #622). */}
        {canWrite && (
          <LgmButton
            variant="outlined"
            onClick={onAddLink}
            style={{ fontSize: '12px', padding: '4px 10px' }}>
            + Link to Orange Book
          </LgmButton>
        )}
      </div>

      <div style={{ padding: '8px 12px' }}>
        <CollapsibleSection
          title={`${records.length} link${records.length === 1 ? '' : 's'}`}
          defaultCollapsed={records.length > 1}>
          {records.map((record, idx) => (
            <div
              key={`${record.fda_product_id}-${idx}`}
              style={{
                borderTop:
                  idx > 0
                    ? `1px solid ${ORANGE_BOOK_PANEL_COLORS.border}`
                    : undefined,
                paddingTop: idx > 0 ? '8px' : undefined,
                marginTop: idx > 0 ? '8px' : undefined,
              }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {visibleFields.map(field => (
                    <FieldRow
                      key={field.key}
                      label={
                        field.key === 'te_code' ? (
                          <>
                            {field.label}
                            <TeCodeHeaderTooltip />
                          </>
                        ) : field.key === 'appl_type' ? (
                          <>
                            {field.label}
                            <ApplTypeHeaderTooltip />
                          </>
                        ) : (
                          field.label
                        )
                      }
                      value={
                        field.key === 'te_code' ? (
                          <TeCodeValue code={record.te_code} />
                        ) : field.key === 'appl_type' ? (
                          <ApplTypeValue code={record.appl_type} />
                        ) : (
                          resolveValue(field, record)
                        )
                      }
                    />
                  ))}
                </div>
                {canWrite && (
                  <LgmButton
                    variant="flat"
                    color="danger"
                    onClick={() =>
                      setBreakTarget({
                        recordId: record.fda_product_id,
                        label: recordLabel(record),
                      })
                    }
                    aria-label={`Break link to Orange Book record ${recordLabel(record)}`}
                    style={{ padding: '4px', minWidth: 'auto', flexShrink: 0 }}>
                    <LinkOffIcon style={{ fontSize: '18px' }} />
                  </LgmButton>
                )}
              </div>

              {/* Secondary (accordion-hidden) fields — independent of the
                  outer CollapsibleSection's collapse state (two levels of
                  nesting, Sub-task 8.2). */}
              <Accordion
                expanded={!!expandedByIndex[idx]}
                onChange={() =>
                  setExpandedByIndex(prev => ({ ...prev, [idx]: !prev[idx] }))
                }
                slotProps={{ transition: { unmountOnExit: false } }}
                disableGutters
                elevation={0}
                sx={{ '&:before': { display: 'none' } }}
                style={{ backgroundColor: 'transparent' }}>
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      style={{
                        color: ORANGE_BOOK_PANEL_COLORS.badgeText,
                        fontSize: '18px',
                      }}
                    />
                  }
                  style={{
                    minHeight: '32px',
                    padding: '0 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: ORANGE_BOOK_PANEL_COLORS.badgeText,
                    backgroundColor: 'transparent',
                  }}>
                  More details
                </AccordionSummary>
                <AccordionDetails
                  style={{
                    padding: '0 12px 8px 12px',
                    backgroundColor: 'transparent',
                  }}>
                  {hiddenFields.map(field => (
                    <FieldRow
                      key={field.key}
                      label={
                        field.key === 'type' ? (
                          <>
                            {field.label}
                            <ObTypeHeaderTooltip />
                          </>
                        ) : (
                          field.label
                        )
                      }
                      value={
                        field.key === 'type' ? (
                          <ObTypeValue code={record.type} />
                        ) : (
                          resolveValue(field, record)
                        )
                      }
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            </div>
          ))}
        </CollapsibleSection>
      </div>

      {breakTarget && (
        <BreakLinkConfirmDialog
          productId={productId}
          source="orange_book"
          sourceLabel="Orange Book"
          recordId={breakTarget.recordId}
          recordLabel={breakTarget.label}
          onClose={() => setBreakTarget(null)}
          onBroken={handleBroken}
        />
      )}
    </div>
  );
};

export default OrangeBookPanel;
